import React from 'react';
import type { RoutesPaths } from '@shared/core';
import { RoutesProvider } from '@shared/core';
import globalWindow from '@shared/core/globals';

/**
 * Example Route name types. Defines the routes that are available
 */
type RoutesNames =
  | 'account'
  | 'accommodations'
  | 'budget'
  | 'card'
  | 'cashfunds'
  | 'checklist'
  | 'collectContacts'
  | 'contact'
  | 'customPage'
  | 'dashboard'
  | 'design'
  | 'domain'
  | 'ecard'
  | 'event'
  | 'eventServiceCenter'
  | 'faq'
  | 'accommodationsStayAreas'
  | 'guestHotelBookings'
  | 'guestList'
  | 'guests'
  | 'home'
  | 'homeCardLab'
  | 'honeymoon'
  | 'hotelBlock'
  | 'hotelReminder'
  | 'shareStayReminder'
  | 'instantRoomBlockMessage'
  | 'hotelSearch'
  | 'ideas'
  | 'inspiration'
  | 'invitations'
  | 'emailTemplates'
  | 'menuIframe'
  | 'mergeGuests'
  | 'message'
  | 'messaging'
  | 'messagingDrafts'
  | 'messagingScheduled'
  | 'messagingSent'
  | 'photoCollection'
  | 'photoCollectionView'
  | 'photoUpload'
  | 'photos'
  | 'planning'
  | 'planningAccommodations'
  | 'postOffice'
  | 'proschedule'
  | 'provip'
  | 'registration'
  | 'registry'
  | 'registryLinks'
  | 'registryManage'
  | 'registryTrack'
  | 'registryInternationalManage'
  | 'registryInternationalTrack'
  | 'rsvp'
  | 'rsvpReminder'
  | 'rsvpSettings'
  | 'savethedate'
  | 'schedule'
  | 'seatingChart'
  | 'settings'
  | 'settingsGeneral'
  | 'settingsMenu'
  | 'settingsSecurity'
  | 'shop'
  | 'story'
  | 'textBlast'
  | 'travel'
  | 'vendorShowcase'
  | 'video'
  | 'vip'
  | 'unifiedWebsiteEditor'
  | 'unifiedWebsiteEditorPages'
  | 'website'
  | 'websiteV2Accommodations'
  | 'websiteOverview'
  | 'websiteSettings'
  | 'welcome'
  | 'whereToStaySettings';

/**
 * Example route paths. Has a path for the <Router /> and a function for history to push to.
 */
export const routePaths: RoutesPaths<RoutesNames> = {
  home: {
    path: '',
    goToPath: () => '',
  },
  cashfunds: {
    path: 'cash-fund',
    goToPath: () => 'cash-fund',
  },
  dashboard: {
    path: 'dashboard',
    goToPath: () => 'dashboard',
  },
  design: {
    path: 'design',
    goToPath: () => 'design',
  },
  checklist: {
    path: 'checklist',
    goToPath: () => 'checklist',
  },
  vendorShowcase: {
    path: 'showcase',
    goToPath: () => 'showcase',
  },
  guests: {
    path: 'guests',
    goToPath: () => 'guests',
  },
  invitations: {
    path: 'invitations',
    goToPath: () => 'invitations',
  },
  photos: {
    path: 'photos',
    goToPath: () => 'photos',
  },
  rsvp: {
    path: 'rsvp',
    goToPath: () => 'rsvp',
  },
  // Retired standalone page — kept only so bookmarks can be redirected into Joy Settings
  // (`/edit/settings/rsvp`). Registered ahead of `rsvp` in `Admin.controller` so the redirect
  // resolves without mounting the RSVP app. @see RedirectToJoySettingsRsvp
  rsvpSettings: {
    path: 'rsvp/settings',
    goToPath: () => 'rsvp/settings',
  },
  registry: {
    path: 'registry',
    goToPath: () => 'registry',
  },
  registryManage: {
    path: 'registry/manage',
    goToPath: () => 'registry/manage',
  },
  registryTrack: {
    path: 'registry/track',
    goToPath: () => 'registry/track',
  },
  registryInternationalManage: {
    path: 'registry/intl/manage',
    goToPath: () => 'registry/intl/manage',
  },
  registryInternationalTrack: {
    path: 'registry/intl/track',
    goToPath: () => 'registry/intl/track',
  },
  inspiration: {
    path: 'inspiration',
    goToPath: () => 'inspiration',
  },
  settings: {
    path: 'settings',
    goToPath: () => 'settings',
  },
  account: {
    path: 'settings/account',
    goToPath: () => 'settings/account',
  },
  settingsSecurity: {
    path: 'settings/security',
    goToPath: () => 'settings/security',
  },
  settingsGeneral: {
    path: 'settings/general',
    goToPath: (section?: string) => (section ? `settings/general#${section}` : 'settings/general'),
  },
  whereToStaySettings: {
    path: 'settings/where-to-stay',
    goToPath: () => 'settings/where-to-stay',
  },
  websiteSettings: {
    path: 'settings/website',
    goToPath: () => 'settings/website',
  },
  settingsMenu: {
    path: 'settings/menu',
    goToPath: () => 'settings/menu',
  },
  video: {
    path: 'video',
    goToPath: () => 'video',
  },
  domain: {
    path: 'domain',
    goToPath: () => 'domain',
  },
  ideas: {
    path: 'ideas',
    goToPath: () => 'ideas',
  },
  postOffice: {
    path: 'email',
    goToPath: () => 'email',
  },
  story: {
    path: 'story',
    goToPath: () => 'story',
  },
  textBlast: {
    path: 'text-blast',
    goToPath: () => 'text-blast',
  },
  schedule: {
    path: 'schedule',
    goToPath: () => 'schedule',
  },
  travel: {
    path: 'travel',
    goToPath: () => 'travel',
  },
  accommodations: {
    path: 'accommodations',
    goToPath: () => 'accommodations',
  },
  guestHotelBookings: {
    path: 'accommodations/guests',
    goToPath: () => 'accommodations/guests',
  },
  accommodationsStayAreas: {
    path: 'accommodations/stay-areas',
    goToPath: () => 'accommodations/stay-areas',
  },
  website: {
    path: 'website/:pageSlug',
    goToPath: (pageSlug: string) => `website/${pageSlug}`,
  },
  unifiedWebsiteEditor: {
    path: 'website',
    goToPath: () => 'website',
  },
  // WebsiteV2: the legacy Where to Stay sidebar as a panel beside the editor embed. The optional
  // `:tab` suffix matches the legacy `/accommodations[/guests|/stay-areas]` tab URLs, which the
  // Accommodations module reads off `location.pathname`. See doc/website-v2/PLAN.md.
  websiteV2Accommodations: {
    path: 'website/accommodations/:tab(stay-areas|guests)?',
    // `tab`: 'stay-areas' | 'guests' (typed `string` — `RoutesPaths` requires string params).
    goToPath: (tab?: string) => (tab ? `website/accommodations/${tab}` : 'website/accommodations'),
  },
  unifiedWebsiteEditorPages: {
    path: 'website/pages',
    goToPath: () => 'website/pages',
  },
  customPage: {
    path: 'website/page/:pageSlug',
    goToPath: (pageSlug: string) => `website/page/${pageSlug}`,
  },
  registryLinks: {
    path: 'registrylinks',
    goToPath: () => 'registrylinks',
  },
  vip: {
    path: 'vip',
    goToPath: () => 'vip',
  },
  faq: {
    path: 'faq',
    goToPath: () => 'faq',
  },
  welcome: {
    path: 'welcome',
    goToPath: () => 'welcome',
  },
  shop: {
    path: 'registry/shop',
    goToPath: () => 'registry/shop',
  },
  ecard: {
    path: 'ecard/:draftType',
    goToPath: (draftType: string) => `ecard/${draftType}`,
  },
  card: {
    path: 'cards',
    goToPath: () => `cards`,
  },
  savethedate: {
    path: 'savethedate',
    goToPath: () => 'savethedate',
  },
  eventServiceCenter: {
    path: 'newservicecenter',
    goToPath: () => 'newservicecenter',
  },
  // Internal review surface for the admin lifecycle cards. Two gates, neither of them this entry:
  // the React route is registered only when the `homeCardLab` flag is on, and rendering is behind
  // `ProtectedSuperUserRoute`. The segment must also stay in the passthrough allowlist in
  // `src/server/server.ts`, or the request never reaches joy-web at all. @see routes/HomeCardLab
  homeCardLab: {
    path: 'home-card-lab',
    goToPath: () => 'home-card-lab',
  },
  planning: {
    path: 'planning',
    goToPath: () => 'planning',
  },
  proschedule: {
    path: 'proschedule',
    goToPath: () => 'proschedule',
  },
  provip: {
    path: 'provip',
    goToPath: () => 'provip',
  },
  hotelBlock: {
    path: 'planning/hotelblock',
    goToPath: () => 'planning/hotelblock',
  },
  hotelSearch: {
    path: 'hotel',
    goToPath: () => 'hotel',
  },
  planningAccommodations: {
    path: 'planning/accommodations',
    goToPath: () => 'planning/accommodations',
  },
  collectContacts: {
    path: 'collect-contacts',
    goToPath: () => 'collect-contacts',
  },
  contact: {
    path: 'contact',
    goToPath: () => 'contact',
  },
  honeymoon: {
    path: 'honeymoon',
    goToPath: () => 'honeymoon',
  },
  hotelReminder: {
    path: 'hotel-reminder',
    goToPath: () => 'hotel-reminder',
  },
  shareStayReminder: {
    path: 'share-stay-reminder',
    goToPath: () => 'share-stay-reminder',
  },
  instantRoomBlockMessage: {
    path: 'instant-room-block-message',
    goToPath: () => 'instant-room-block-message',
  },
  rsvpReminder: {
    path: 'send-rsvp-reminder',
    goToPath: () => 'send-rsvp-reminder',
  },
  event: {
    path: 'event',
    goToPath: () => 'event',
  },
  websiteOverview: {
    path: 'website/overview',
    goToPath: () => 'website/overview',
  },
  photoCollection: {
    path: 'website/photos',
    goToPath: () => 'website/photos',
  },
  photoCollectionView: {
    path: 'website/photos/:photoId',
    goToPath: (photoId: string) => `website/photos/${photoId}`,
  },
  photoUpload: {
    path: 'photo-upload',
    goToPath: () => 'photo-upload',
  },
  messaging: {
    path: 'messaging',
    goToPath: () => 'messaging',
  },
  emailTemplates: {
    path: 'messaging/templates',
    goToPath: () => 'messaging/templates',
  },
  messagingDrafts: {
    path: 'messaging/drafts',
    goToPath: () => 'messaging/drafts',
  },
  messagingScheduled: {
    path: 'messaging/scheduled',
    goToPath: () => 'messaging/scheduled',
  },
  messagingSent: {
    path: 'messaging/sent',
    goToPath: () => 'messaging/sent',
  },
  message: {
    path: 'message/:type',
    goToPath: (type: string) => `message/${type}`,
  },
  guestList: {
    path: 'guestlist',
    goToPath: () => 'guestlist',
  },
  mergeGuests: {
    path: 'guests/merge',
    goToPath: () => 'guests/merge',
  },
  // SPA shell that iframes the S3-served seating-chart MFE. The segment is deliberately NOT
  // `seating-chart`: the server streams that path from S3 (src/server/server.ts) before the SPA
  // catch-all, so a same-named client route would never render.
  seatingChart: {
    path: 'seatingchart',
    goToPath: () => 'seatingchart',
  },
  // SPA shell that iframes the S3-served budget-tracker MFE. The segment is deliberately NOT
  // `budget-tracker`: the server streams that path from S3 (src/server/server.ts) before the SPA
  // catch-all, so a same-named client route would never render.
  budget: {
    path: 'budget',
    goToPath: () => 'budget',
  },
  // Chrome-less hamburger menu for same-origin iframe embeds by other Joy apps.
  // @see components/UniversalBurgerMenu/embed/BurgerMenuEmbed
  menuIframe: {
    path: 'menu-iframe',
    goToPath: () => 'menu-iframe',
  },
  registration: {
    path: 'registration',
    goToPath: () => 'registration',
  },
};

export const Router: React.FC<{ eventHandle: string }> = ({ eventHandle, children }) => {
  return <RoutesProvider handle={eventHandle}>{children}</RoutesProvider>;
};

export const monorepoRoutePaths = {
  accountPersonalInfo: 'account/personal-info',
  accountVerifyEmail: `/account/verify-email?prev=${globalWindow.location?.href}?dialog=verifyEmail`,
  eCardChangeTheDate: 'ecard/changethedate',
  emailCreate: 'email/create',
  eCardCustom: 'ecard/custom',
  eCardInvitations: 'ecard/invitation',
  eCardSaveTheDate: 'ecard/savethedate',
  guests: 'guests',
  photos: 'photos',
  registryShop: 'registry/shop',
  registryOverview: 'registry',
  registryManage: 'registry/manage',
  registryTrack: 'registry/track',
  registryInternationalManage: 'registry/intl/manage',
  registryInternationalTrack: 'registry/intl/track',
  registryInternationalAddGifts: 'registry/intl/add-gifts',
  rsvp: 'rsvp',
  rsvpAnswers: 'rsvp/answers',
  rsvpActivity: 'rsvp/activity',
  rsvpSettings: 'rsvp/settings',
  settings: 'settings',
  settingsAccount: 'settings/account',
  settingsGeneral: 'settings/general',
  settingsMenu: 'settings/menu',
  settingsSecurity: 'settings/security',
  video: 'video',
  writersblock: 'writersblock',
};
